import { FormControlLabel, Grid, Switch, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useFormik } from 'formik';
import AryAccountsLazyAutoComplete from 'framework/base/components/AryAccountsLazyAutoComplete';
import AryCurrencyTextField from 'framework/base/components/AryCurrencyTextField';
import AryDatePicker from 'framework/base/components/AryDatePicker';
import AryLoadingButton from 'framework/base/components/AryLoadingButton';
import ArySelectTransactionGroups from 'framework/base/components/ArySelectTransactionGroups';
import AryWrapper from 'framework/base/components/AryWrapper';
import { GET_ACCOUNTS } from 'framework/base/constants/aryGraphqlSchemas';
import arySystemSettings from 'framework/base/constants/arySystemSettings';
import useSystemSettingsContext from 'framework/base/hooks/useSystemSettingsContext';
import { assignInitialValue } from 'framework/base/utils/Utils';
import AryLoadingAnimation from 'framework/desktop/AryLoadingAnimation';
import AryFileUploaderModal from 'framework/desktop/AryNewFileUploaderModal';
import AccountStatus from 'framework/desktop/opponent-type-components/AccountStatus';
import CommissionComponentChip from 'framework/desktop/switchable-chips/CommissionComponentChip';
import PrivateIcon from 'framework/desktop/switchable-chips/SVGs/PrivateIcon';
import SyncCreditLimitIcon from 'framework/desktop/switchable-chips/SVGs/SyncCreditLimitIcon';
import SwitchableChip from 'framework/desktop/switchable-chips/SwitchableChip';
import WhatsappChips from 'framework/desktop/switchable-chips/WhatsappChips';
import {
  transferBetweenAccountsValidationSchema,
  validateTransferBetweenAccountsLogical
} from 'pages/validations/aryFormValidations';
import React, {
  Suspense,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAuth } from 'spec';
import ReceiptModal from './ReceiptModal';

export const FORMIK_VALUES = {
  transactionId: 'transactionId',
  date: 'date',
  isPrivate: 'isPrivate',
  debtorAccountNumber: 'debtorAccountNumber',
  creditorAccountNumber: 'creditorAccountNumber',
  amount: 'amount',
  currencyCode: 'currencyCode',
  description: 'description',
  isPublishToWhatsApp: 'isPublishToWhatsApp',
  shouldSyncCreditLimit: 'shouldSyncCreditLimit',
  hasDebtorCommission: 'hasDebtorCommission',
  hasCreditorCommission: 'hasCreditorCommission',
  debtorCommissionAmount: 'debtorCommissionAmount',
  debtorCommissionCurrencyCode: 'debtorCommissionCurrencyCode',
  debtorCommissionIsDebtor: 'debtorCommissionIsDebtor',
  creditorCommissionAmount: 'creditorCommissionAmount',
  creditorCommissionCurrencyCode: 'creditorCommissionCurrencyCode',
  creditorCommissionIsDebtor: 'creditorCommissionIsDebtor',
  files: 'files',
  fileIds: 'fileIds',
  debtorAccountTitle: 'debtorAccountTitle',
  creditorAccountTitle: 'creditorAccountTitle',
  purposeOfExchange: 'purposeOfExchange',
  transactionCategory: 'transactionCategory'
};

const TransferBetweenAccountsBase = forwardRef(
  (
    {
      initialValues = {},
      removedFileIds = [],
      handleChangeRemovedFileIds,
      onSubmit,
      submitButtonLoading,
      submitButtonLabel,
      onReceiptModalClose,
      handleCloseParentModal,
      handleChangeAccountNumber,
      transactionId,
      hideRepeatOperation,
      submitButtonSize = 'medium',
      disabledValues = [],
      fieldsWithAllAccountType = [],
      isDebtorAccountCredit,
      isCreditorAccountCredit,
      hiddenCommissions = [],
      disabledSelectGroup,
      disabledTransactionPurpose
    },
    ref
  ) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { systemSettings } = useSystemSettingsContext();
    const auth = useAuth();
    const whatsappLoginState = useSelector(
      (state) => state.systemReducer.whatsapp.isLogin
    );

    const hasWhatsappAccess = auth.WhatsappA;
    const { PrivateTransactionR, IsAfghanVersion } = auth;

    const debtorAccountInfoRef = useRef(null);
    const creditorAccountInfoRef = useRef(null);
    const [receiptModalOpen, setReceiptModalOpen] = useState(false);
    const [debtorAccountType, setDebtorAccountType] = useState('');
    const [creditorAccountType, setCreditorAccountType] = useState('');
    const [isCalculationDone, setIsCalculationDone] = useState(true);
    const formik = useFormik({
      initialValues: {
        [FORMIK_VALUES.transactionId]: assignInitialValue(
          initialValues,
          FORMIK_VALUES.transactionId,
          undefined
        ),
        [FORMIK_VALUES.date]: assignInitialValue(
          initialValues,
          FORMIK_VALUES.date,
          new Date()
        ),
        [FORMIK_VALUES.isPrivate]: assignInitialValue(
          initialValues,
          FORMIK_VALUES.isPrivate,
          false
        ),
        [FORMIK_VALUES.debtorAccountNumber]: assignInitialValue(
          initialValues,
          FORMIK_VALUES.debtorAccountNumber,
          ''
        ),
        [FORMIK_VALUES.creditorAccountNumber]: assignInitialValue(
          initialValues,
          FORMIK_VALUES.creditorAccountNumber,
          ''
        ),
        [FORMIK_VALUES.amount]: assignInitialValue(
          initialValues,
          FORMIK_VALUES.amount,
          ''
        ),
        [FORMIK_VALUES.currencyCode]: assignInitialValue(
          initialValues,
          FORMIK_VALUES.currencyCode,
          systemSettings[arySystemSettings.mainCurrency]
        ),
        [FORMIK_VALUES.description]: assignInitialValue(
          initialValues,
          FORMIK_VALUES.description,
          ''
        ),
        [FORMIK_VALUES.shouldSyncCreditLimit]: assignInitialValue(
          initialValues,
          FORMIK_VALUES.defaultUpdateCreditLimit,
          systemSettings[arySystemSettings.defaultUpdateCreditLimit]
        ),
        [FORMIK_VALUES.hasDebtorCommission]: assignInitialValue(
          initialValues,
          FORMIK_VALUES.hasDebtorCommission,
          false
        ),
        [FORMIK_VALUES.hasCreditorCommission]: assignInitialValue(
          initialValues,
          FORMIK_VALUES.hasCreditorCommission,
          false
        ),
        [FORMIK_VALUES.debtorCommissionAmount]: assignInitialValue(
          initialValues,
          FORMIK_VALUES.debtorCommissionAmount,
          ''
        ),
        [FORMIK_VALUES.debtorCommissionCurrencyCode]: assignInitialValue(
          initialValues,
          FORMIK_VALUES.debtorCommissionCurrencyCode,
          systemSettings[arySystemSettings.mainCurrency]
        ),
        [FORMIK_VALUES.debtorCommissionIsDebtor]: assignInitialValue(
          initialValues,
          FORMIK_VALUES.debtorCommissionIsDebtor,
          true
        ),
        [FORMIK_VALUES.creditorCommissionAmount]: assignInitialValue(
          initialValues,
          FORMIK_VALUES.creditorCommissionAmount,
          ''
        ),
        [FORMIK_VALUES.creditorCommissionCurrencyCode]: assignInitialValue(
          initialValues,
          FORMIK_VALUES.creditorCommissionCurrencyCode,
          systemSettings[arySystemSettings.mainCurrency]
        ),
        [FORMIK_VALUES.creditorCommissionIsDebtor]: assignInitialValue(
          initialValues,
          FORMIK_VALUES.creditorCommissionIsDebtor,
          true
        ),
        [FORMIK_VALUES.isPublishToWhatsApp]: assignInitialValue(
          initialValues,
          FORMIK_VALUES.defaultSendWhatsApp,
          systemSettings[arySystemSettings.defaultSendWhatsApp] &&
          whatsappLoginState
        ),
        [FORMIK_VALUES.files]: assignInitialValue(
          initialValues,
          FORMIK_VALUES.files,
          []
        ),
        [FORMIK_VALUES.fileIds]: assignInitialValue(
          initialValues,
          FORMIK_VALUES.fileIds,
          []
        ),
        [FORMIK_VALUES.purposeOfExchange]: assignInitialValue(
          initialValues,
          FORMIK_VALUES.purposeOfExchange,
          []
        ),
        [FORMIK_VALUES.transactionCategory]: assignInitialValue(
          initialValues,
          FORMIK_VALUES.transactionCategory,
          []
        ),
        hideWhatsappAccountInfo: false
      },
      validationSchema: transferBetweenAccountsValidationSchema(
        t,
        isDebtorAccountCredit,
        isCreditorAccountCredit
      ),
      onSubmit: (values) => {
        if (
          !validateTransferBetweenAccountsLogical(
            values,
            formik.setFieldError,
            t,
            true
          )
        ) {
          return;
        }
        onSubmit(values);
      }
    });

    useImperativeHandle(ref, () => ({
      resetForm() {
        formik.resetForm();
      },
      handleOpenReceiptModal() {
        setReceiptModalOpen(true);
      }
    }));

    const handleResetForm = () => {
      // reset form except for the account numbers
      const { debtorAccountNumber, creditorAccountNumber, date } =
        formik.values;
      formik.resetForm();
      formik.setFieldValue(FORMIK_VALUES.date, date);
      formik.setFieldValue(
        FORMIK_VALUES.debtorAccountNumber,
        debtorAccountNumber
      );
      formik.setFieldValue(
        FORMIK_VALUES.creditorAccountNumber,
        creditorAccountNumber
      );
    };

    return (
      <Grid item flexGrow={1} mt={theme.margin.pageContentTop}>
        <form onSubmit={formik.handleSubmit}>
          <Grid
            container
            justifyContent="center"
            alignItems="center"
          >
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              rowSpacing={theme.padding.gridRowSpacing}
              columnSpacing={theme.padding.gridColumnSpacing}
              maxHeight="calc(100vh - 12rem)"
              overflow="auto"
            >
              <AryWrapper>
                <Grid
                  container
                  rowSpacing={theme.padding.gridSmallRowSpacing}
                  columnSpacing={theme.padding.gridSmallColumnSpacing}
                >
                  <Grid item xs={12}>
                    <Grid
                      container
                      columnSpacing={theme.padding.gridSmallColumnSpacing}
                    >
                      <Grid item xs={6}>
                        <AryDatePicker
                          label={t('Date')}
                          value={formik.values.date}
                          autoFocus
                          disabled={disabledValues.includes(FORMIK_VALUES.date)}
                          onChange={(newValue) => {
                            formik.setFieldValue('date', newValue);
                          }}
                          error={formik.touched.date && !!formik.errors.date}
                          helperText={formik.touched.date && formik.errors.date}
                          onBlur={() => formik.setFieldTouched('date', true)}
                          sx={{ height: '2.25rem' }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    {isDebtorAccountCredit ? (
                      <Grid item xs={6}>
                        <TextField
                          label={t('debtor')}
                          disabled
                          fullWidth
                          value={
                            initialValues[FORMIK_VALUES.debtorAccountTitle]
                          }
                        />
                      </Grid>
                    ) : (
                      <Grid
                        container
                        columnSpacing={theme.padding.gridSmallColumnSpacing}
                      >
                        <Grid item xs={6}>
                          <AryAccountsLazyAutoComplete
                            label={t('debtor')}
                            accountType={
                              IsAfghanVersion
                                ? GET_ACCOUNTS.accountType
                                  .TRANSACTABLE_SYSTEM_ACCOUNTS
                                : fieldsWithAllAccountType.includes(
                                  FORMIK_VALUES.debtorAccountNumber
                                )
                                  ? GET_ACCOUNTS.accountType.ALL
                                  : GET_ACCOUNTS.accountType.ALL_CAN_TRANSACT
                            }
                            // disabled={disabledValues.includes(
                            //   FORMIK_VALUES.debtorAccountNumber
                            // )}
                            value={formik.values.debtorAccountNumber}
                            onChange={(selectedObject) => {
                              formik.setFieldValue(
                                'debtorAccountNumber',
                                selectedObject?.value || ''
                              );
                            }}
                            handleChangeTypeTitle={(typeTitle) =>
                              setDebtorAccountType(typeTitle)
                            }
                            excludedAccountNumber={
                              formik.values.creditorAccountNumber
                            }
                            error={
                              formik.touched.debtorAccountNumber &&
                              !!formik.errors.debtorAccountNumber
                            }
                            helperText={
                              formik.touched.debtorAccountNumber &&
                              formik.errors.debtorAccountNumber
                            }
                            onBlur={() =>
                              formik.setFieldTouched(
                                'debtorAccountNumber',
                                true
                              )
                            }
                          />
                        </Grid>
                        {/* {formik.values.debtorAccountNumber &&
                          formik.values.currencyCode && ( */}
                        <Grid item xs={6}>
                          <AccountStatus
                            accountNumber={formik.values.debtorAccountNumber}
                            currencyCode={formik.values.currencyCode}
                            isCalculationDoneHandler={(newValue) =>
                              setIsCalculationDone(newValue)
                            }
                            ref={debtorAccountInfoRef}
                            message={t('AccountStatusMessage')}
                          />
                        </Grid>
                        {/* )} */}
                      </Grid>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    {isCreditorAccountCredit ? (
                      <Grid item xs={6}>
                        <TextField
                          label={t('creditor')}
                          disabled
                          fullWidth
                          value={
                            initialValues[FORMIK_VALUES.creditorAccountTitle]
                          }
                        />
                      </Grid>
                    ) : (
                      <Grid
                        container
                        columnSpacing={theme.padding.gridSmallColumnSpacing}
                      >
                        <Grid item xs={6}>
                          <AryAccountsLazyAutoComplete
                            label={t('creditor')}
                            accountType={
                              IsAfghanVersion
                                ? GET_ACCOUNTS.accountType
                                  .TRANSACTABLE_SYSTEM_ACCOUNTS
                                : fieldsWithAllAccountType.includes(
                                  FORMIK_VALUES.creditorAccountNumber
                                )
                                  ? GET_ACCOUNTS.accountType.ALL
                                  : GET_ACCOUNTS.accountType.ALL_CAN_TRANSACT
                            }
                            // disabled={disabledValues.includes(
                            //   FORMIK_VALUES.creditorAccountNumber
                            // )}
                            handleChangeTypeTitle={(typeTitle) =>
                              setCreditorAccountType(typeTitle)
                            }
                            excludedAccountNumber={
                              formik.values.debtorAccountNumber
                            }
                            value={formik.values.creditorAccountNumber}
                            onChange={(selectedObject) => {
                              formik.setFieldValue(
                                'creditorAccountNumber',
                                selectedObject?.value || ''
                              );
                            }}
                            error={
                              formik.touched.creditorAccountNumber &&
                              !!formik.errors.creditorAccountNumber
                            }
                            helperText={
                              formik.touched.creditorAccountNumber &&
                              formik.errors.creditorAccountNumber
                            }
                            onBlur={() =>
                              formik.setFieldTouched(
                                'creditorAccountNumber',
                                true
                              )
                            }
                          />
                        </Grid>
                        {/* {formik.values.currencyCode &&
                          formik.values.creditorAccountNumber && ( */}
                        <Grid item xs={6}>
                          <AccountStatus
                            accountNumber={formik.values.creditorAccountNumber}
                            currencyCode={formik.values.currencyCode}
                            isCalculationDoneHandler={(newValue) =>
                              setIsCalculationDone(newValue)
                            }
                            ref={creditorAccountInfoRef}
                            message={t('AccountStatusMessage')}
                          />
                        </Grid>
                        {/* )} */}
                      </Grid>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Grid
                      container
                      columnSpacing={theme.padding.gridSmallColumnSpacing}
                    >
                      <Grid item xs={6}>
                        <AryCurrencyTextField
                          label={t('Transaction Amount')}
                          fullWidth
                          disabled={disabledValues.includes(
                            FORMIK_VALUES.amount
                          )}
                          currencyDisabled={disabledValues.includes(
                            FORMIK_VALUES.currencyCode
                          )}
                          value={formik.values.amount}
                          onChange={({ numberValue }) => {
                            formik.setFieldValue('amount', numberValue);
                          }}
                          currencyLabel={t('Transaction Currency')}
                          currencyValue={formik.values.currencyCode}
                          onCurrencyChange={(selectedObj) =>
                            formik.setFieldValue(
                              'currencyCode',
                              selectedObj?.value || ''
                            )
                          }
                          error={
                            formik.touched.amount && !!formik.errors.amount
                          }
                          helperText={
                            formik.touched.amount && formik.errors.amount
                          }
                          onBlur={() => formik.setFieldTouched('amount', true)}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label={t('Description')}
                          fullWidth
                          autoComplete="off"
                          value={formik.values.description}
                          onChange={(event) =>
                            formik.setFieldValue(
                              'description',
                              event.target.value.trimStart()
                            )
                          }
                          error={
                            formik.touched.description &&
                            !!formik.errors.description
                          }
                          helperText={
                            formik.touched.description &&
                            formik.errors.description
                          }
                          onBlur={() =>
                            formik.setFieldTouched('description', true)
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  {debtorAccountType !== GET_ACCOUNTS.typeTitle.CHARGING_SALE &&
                    !hiddenCommissions.includes(
                      FORMIK_VALUES.debtorCommissionAmount
                    ) && (
                      <Grid item xs={6}>
                        <AryCurrencyTextField
                          label={t('Debtor Commission')}
                          fullWidth
                          name="debtorCommissionAmount"
                          // amount
                          value={formik.values.debtorCommissionAmount}
                          onChange={({ numberValue }) => {
                            formik.setFieldValue(
                              'debtorCommissionAmount',
                              numberValue
                            );
                            if (numberValue > 0) {
                              formik.setFieldValue('hasDebtorCommission', true);
                            } else {
                              formik.setFieldValue(
                                'hasDebtorCommission',
                                false
                              );
                            }
                          }}
                          // currency
                          currencyLabel={t('Commission Currency')}
                          currencyValue={
                            formik.values.debtorCommissionCurrencyCode
                          }
                          onCurrencyChange={(selectedObj) => {
                            formik.setFieldValue(
                              'debtorCommissionCurrencyCode',
                              selectedObj?.value || ''
                            );
                          }}
                          // commission
                          isCommission
                          commissionComponent={
                            <CommissionComponentChip
                              value={formik.values.debtorCommissionIsDebtor}
                              handleClick={() =>
                                formik.setFieldValue(
                                  'debtorCommissionIsDebtor',
                                  !formik.values.debtorCommissionIsDebtor
                                )
                              }
                              trueValueLabel={t('Deposit')}
                              falseValueLabel={t('Withdraw')}
                            />
                          }
                          error={
                            formik.touched.debtorCommissionAmount &&
                            !!formik.errors.debtorCommissionAmount
                          }
                          helperText={
                            (formik.touched.debtorCommissionAmount &&
                              formik.errors.debtorCommissionAmount) ||
                            t('CommissionAmountRegisteredToDebtor')
                          }
                          onBlur={() =>
                            formik.setFieldTouched(
                              'debtorCommissionAmount',
                              true
                            )
                          }
                        />
                      </Grid>
                    )}
                  {creditorAccountType !==
                    GET_ACCOUNTS.typeTitle.CHARGING_SALE &&
                    !hiddenCommissions.includes(
                      FORMIK_VALUES.creditorCommissionAmount
                    ) && (
                      <Grid item xs={6}>
                        <AryCurrencyTextField
                          label={t('Creditor Commission')}
                          fullWidth
                          name="creditorCommissionAmount"
                          // amount
                          value={formik.values.creditorCommissionAmount}
                          onChange={({ numberValue }) => {
                            formik.setFieldValue(
                              'creditorCommissionAmount',
                              numberValue
                            );
                            if (numberValue > 0) {
                              formik.setFieldValue(
                                'hasCreditorCommission',
                                true
                              );
                            } else {
                              formik.setFieldValue(
                                'hasCreditorCommission',
                                false
                              );
                            }
                          }}
                          // currency
                          currencyLabel={t('Commission Currency')}
                          currencyValue={
                            formik.values.creditorCommissionCurrencyCode
                          }
                          onCurrencyChange={(selectedObj) =>
                            formik.setFieldValue(
                              'creditorCommissionCurrencyCode',
                              selectedObj?.value || ''
                            )
                          }
                          // commission
                          isCommission
                          commissionComponent={
                            <CommissionComponentChip
                              value={formik.values.creditorCommissionIsDebtor}
                              handleClick={() =>
                                formik.setFieldValue(
                                  'creditorCommissionIsDebtor',
                                  !formik.values.creditorCommissionIsDebtor
                                )
                              }
                              trueValueLabel={t('Deposit')}
                              falseValueLabel={t('Withdraw')}
                            />
                          }
                          error={
                            formik.touched.creditorCommissionAmount &&
                            !!formik.errors.creditorCommissionAmount
                          }
                          helperText={
                            (formik.touched.creditorCommissionAmount &&
                              formik.errors.creditorCommissionAmount) ||
                            t('CommissionAmountRegisteredToCreditor')
                          }
                          onBlur={() =>
                            formik.setFieldTouched(
                              'creditorCommissionAmount',
                              true
                            )
                          }
                        />
                      </Grid>
                    )}
                </Grid>
              </AryWrapper>
              {hasWhatsappAccess &&
                <AryWrapper style={{ marginTop: '1.25rem' }}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!formik.values.hideWhatsappAccountInfo}
                          onChange={(e) => formik.setFieldValue('hideWhatsappAccountInfo', !e.target.checked)}
                        />
                      }
                      label={t('sendAccountNameInWhatsapp')}
                    />
                  </Grid>
                </AryWrapper>
              }
              <AryWrapper style={{ marginTop: '1.25rem' }}>
                <Grid
                  container
                  rowSpacing={theme.padding.gridSmallRowSpacing}
                  columnSpacing={theme.padding.gridSmallColumnSpacing}
                >
                  <Grid item xs={12} display="flex" gap={4}>
                    <Grid item>
                      <SwitchableChip
                        handleClick={() =>
                          formik.setFieldValue(
                            'shouldSyncCreditLimit',
                            !formik.values.shouldSyncCreditLimit
                          )
                        }
                        value={formik.values.shouldSyncCreditLimit}
                        title={t('SyncCreditLimit')}
                        Icon={SyncCreditLimitIcon}
                      />
                    </Grid>
                    {PrivateTransactionR && (
                      <Grid item>
                        <SwitchableChip
                          handleClick={() =>
                            formik.setFieldValue(
                              'isPrivate',
                              !formik.values.isPrivate
                            )
                          }
                          value={formik.values.isPrivate}
                          title={t('TransactionBeWithoutDescription')}
                          Icon={PrivateIcon}
                        />
                      </Grid>
                    )}
                    {hasWhatsappAccess && (
                      <Grid item>
                        <Grid item xs={12} pt="0 !important">
                          <WhatsappChips
                            isPublishToWhatsAppValue={
                              formik.values.isPublishToWhatsApp
                            }
                            handleChangeIsPublishToWhatsapp={(newValue) => {
                              formik.setFieldValue(
                                'isPublishToWhatsApp',
                                newValue
                              );
                            }}
                          />
                        </Grid>
                      </Grid>
                    )}
                    <Grid item>
                      <AryFileUploaderModal
                        files={formik.values.files}
                        onUploadFiles={(newFiles, newFileIds) => {
                          formik.setFieldValue(FORMIK_VALUES.files, newFiles);
                          formik.setFieldValue(
                            FORMIK_VALUES.fileIds,
                            newFileIds
                          );
                        }}
                        fileIds={formik.values.fileIds}
                        handleRemovedFileIds={handleChangeRemovedFileIds}
                        removedFileIds={removedFileIds}
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={6}>
                    <ArySelectTransactionGroups
                      label={t('SelectGroup')}
                      isClearable
                      multiple
                      disabled={disabledSelectGroup}
                      transactionCategories={formik.values.transactionCategory}
                      onTransactionCategoriesChange={(newValues) => {
                        formik.setFieldValue(
                          'transactionCategory',
                          newValues.map((newVal) => newVal.value)
                        );
                      }}
                    />
                  </Grid>
                  <Grid item xs>
                    <TextField
                      label={t('Purpose Of Exchange')}
                      fullWidth
                      autoComplete="off"
                      value={formik.values.purposeOfExchange}
                      disabled={disabledTransactionPurpose}
                      onChange={(event) =>
                        formik.setFieldValue(
                          'purposeOfExchange',
                          event.target.value.trimStart()
                        )
                      }
                    />
                  </Grid>
                </Grid>
              </AryWrapper>
            </Grid>
            <Grid item xs={12} textAlign="right" mt={theme.padding.gridRowSpacing}>
              <AryLoadingButton
                loading={submitButtonLoading}
                size={submitButtonSize}
                variant="contained"
                type="submit"
                disabled={!isCalculationDone}
              >
                {submitButtonLabel}
              </AryLoadingButton>
            </Grid>
          </Grid>
        </form>
        {receiptModalOpen && (
          <Suspense fallback={<AryLoadingAnimation />}>
            <ReceiptModal
              open={receiptModalOpen}
              onReceiptModalClose={onReceiptModalClose}
              handleClose={() => {
                handleResetForm();

                setReceiptModalOpen(false);
                if (handleCloseParentModal) {
                  handleCloseParentModal();
                }
              }}
              handleRepeatOperation={() => {
                setReceiptModalOpen(false);
                debtorAccountInfoRef.current?.getAccountInformation();
                creditorAccountInfoRef.current?.getAccountInformation();

                handleResetForm();
              }}
              handleChangeAccountNumber={handleChangeAccountNumber}
              debtorAccountNumber={
                formik.values[FORMIK_VALUES.debtorAccountNumber]
              }
              creditorAccountNumber={
                formik.values[FORMIK_VALUES.creditorAccountNumber]
              }
              hideRepeatOperation={hideRepeatOperation}
              transactionId={transactionId}
            />
          </Suspense>
        )}
      </Grid>
    );
  }
);

export default TransferBetweenAccountsBase;
